import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Braces, ArrowLeft, HelpCircle } from 'lucide-react';
import { useLanguage } from '../../i18n';

/**
 * JSON工具帮助文档页面
 */
function JsonHelp() {
  const { language } = useLanguage();
  const [activeSection, setActiveSection] = useState(0);

  // 监听滚动，高亮当前章节
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id^="section-"]');
      let currentSection = 0;

      sections.forEach((section, index) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 100 && rect.bottom > 100) {
          currentSection = index;
        }
      });

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // 初始调用

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const content = {
    zh: {
      title: 'JSON格式化工具使用指南',
      subtitle: '快速掌握DevToolkit的JSON工具',
      sections: [
        {
          title: '功能特性',
          items: [
            {
              title: 'JSON转表格视图 (JSON to HTML Table)',
              desc: '将JSON数据转换为类似Postman的表格展示方式，以键(Key)、值(Value)、类型(Type)三列清晰展示。特别适合查看API响应数据、数据库查询结果等复杂JSON结构。支持展开/折叠嵌套对象和数组，一键复制路径和值。表格模式下可以快速定位目标字段，大幅提升数据查看效率。'
            },
            {
              title: '树状视图',
              desc: '传统的树状结构展示，直观显示JSON层级关系。可以快速了解数据结构和嵌套层级。'
            },
            {
              title: '智能错误修复',
              desc: '自动检测常见的JSON格式错误（尾随逗号、单引号、缺少引号等），一键修复所有问题。'
            },
            {
              title: '多标签页对比',
              desc: '支持同时打开多个JSON文件，方便对比不同API返回的数据结构或查看数据变化。'
            },
            {
              title: '内层JSON解析',
              desc: '智能识别字符串形式的JSON，可以进一步解析嵌套的JSON数据。'
            },
            {
              title: '语法高亮',
              desc: '代码编辑器支持JSON语法高亮，不同数据类型用不同颜色显示，提高可读性。'
            }
          ]
        },
        {
          title: '使用教程',
          content: `
            <h3>1. 基础使用</h3>
            <p><strong>步骤 1：粘贴或输入JSON</strong></p>
            <p>在左侧编辑器中粘贴您的JSON数据。支持任何有效的JSON格式，包括对象、数组、嵌套结构等。</p>

            <p><strong>步骤 2：自动格式化</strong></p>
            <p>粘贴后工具会自动检测和格式化JSON。如果发现格式错误，会显示错误提示和修复建议。</p>

            <p><strong>步骤 3：选择查看方式</strong></p>
            <ul>
              <li><strong>表格视图：</strong>点击"表格"标签，以Postman风格查看数据</li>
              <li><strong>树状视图：</strong>点击"树状"标签，查看层级结构</li>
              <li><strong>代码视图：</strong>在编辑器中直接查看格式化后的代码</li>
            </ul>

            <h3>2. 表格视图使用技巧</h3>
            <p><strong>展开/折叠：</strong></p>
            <ul>
              <li>点击对象或数组前的箭头图标可以展开/折叠</li>
              <li>嵌套层级会用缩进显示</li>
            </ul>

            <p><strong>复制功能：</strong></p>
            <ul>
              <li>悬停在键名上，点击复制图标可复制完整路径（如 <code>user.address.city</code>）</li>
              <li>悬停在值上，点击复制图标可复制值内容</li>
            </ul>

            <p><strong>类型识别：</strong></p>
            <ul>
              <li>不同类型用不同颜色标识（字符串、数字、布尔值、对象、数组、null）</li>
              <li>类型列显示具体的数据类型</li>
            </ul>

            <h3>3. 错误修复功能</h3>
            <p><strong>自动检测：</strong></p>
            <p>工具会自动检测以下常见错误：</p>
            <ul>
              <li>尾随逗号（最后一个属性后多余的逗号）</li>
              <li>单引号（JSON只支持双引号）</li>
              <li>键名缺少引号</li>
              <li>注释（JSON不支持注释）</li>
              <li>无效的数字格式</li>
            </ul>

            <p><strong>一键修复：</strong></p>
            <p>当检测到错误时，点击"修复"按钮，工具会自动修复所有可修复的错误。</p>

            <h3>4. 多标签页功能</h3>
            <p><strong>新建标签页：</strong></p>
            <p>点击顶部的"+"按钮创建新标签页，可以同时处理多个JSON文件。</p>

            <p><strong>切换标签页：</strong></p>
            <p>点击标签页名称切换到对应的JSON数据。</p>

            <p><strong>重命名标签页：</strong></p>
            <p>双击标签页名称可以重命名，方便识别不同的JSON数据。</p>

            <p><strong>关闭标签页：</strong></p>
            <p>点击标签页上的"×"按钮关闭不需要的标签页。</p>

            <h3>5. 内层JSON解析</h3>
            <p>当JSON的某个值本身是JSON字符串时：</p>
            <ul>
              <li>在表格视图中，该值会显示为字符串</li>
              <li>点击"解析内层JSON"按钮</li>
              <li>工具会识别并解析字符串中的JSON</li>
              <li>解析后可以继续查看嵌套的结构</li>
            </ul>

            <p><strong>应用场景：</strong></p>
            <p>某些API会返回双重编码的JSON，例如：</p>
            <pre><code>{
  "data": "{\\"name\\":\\"张三\\",\\"age\\":25}"
}</code></pre>
            <p>使用内层解析功能可以查看实际的数据结构。</p>
          `
        },
        {
          title: '常见问题',
          faqs: [
            {
              q: 'JSON 数据会上传到服务器吗？',
              a: '不会。所有 JSON 数据完全在您的浏览器本地处理，不会上传到任何服务器。您的数据完全安全和私密。'
            },
            {
              q: '支持多大的 JSON 文件？',
              a: '理论上没有大小限制，但建议不超过 10MB。太大的文件可能导致浏览器卡顿。对于超大文件，建议先进行分割处理。'
            },
            {
              q: '如何保存格式化后的 JSON？',
              a: '直接从编辑器中复制格式化后的 JSON 代码即可。或者使用浏览器的"另存为"功能保存页面。历史记录会自动保存在本地浏览器中。'
            },
            {
              q: '为什么我的 JSON 无法解析？',
              a: 'JSON 有严格的格式要求。常见问题包括：尾随逗号、使用单引号、键名没有引号、包含注释等。使用我们的错误修复功能可以自动修复大部分问题。'
            },
            {
              q: '表格视图和树状视图有什么区别？',
              a: '表格视图类似 Postman，适合查看具体的键值对和数据类型，信息密度高。树状视图更直观地展示层级关系，适合理解数据结构。建议：首次查看用树状视图，查找具体值用表格视图。'
            },
            {
              q: '如何处理包含中文的 JSON？',
              a: '工具完全支持 Unicode 字符，包括中文、日文、韩文等。您可以直接粘贴包含中文的 JSON，无需进行任何转换。'
            },
            {
              q: '可以编辑 JSON 吗？',
              a: '可以。在代码编辑器中直接编辑 JSON，修改后会实时更新表格视图和树状视图。如果编辑导致格式错误，会显示错误提示。'
            },
            {
              q: '历史记录保存在哪里？',
              a: '历史记录保存在浏览器的本地存储（LocalStorage）中，不会上传到服务器。清除浏览器数据会删除历史记录。'
            },
            {
              q: '支持 JSON5 格式吗？',
              a: '目前暂不支持 JSON5。工具遵循标准 JSON 格式（RFC 7159）。如果您的数据是 JSON5 格式（包含注释、尾随逗号等），请先使用错误修复功能转换为标准 JSON。'
            },
            {
              q: '移动端可以使用吗？',
              a: '可以。网站完全响应式设计，在手机和平板上都可以正常使用。移动端会自动调整布局以适应小屏幕。'
            }
          ]
        },
        {
          title: '使用场景',
          content: `
            <h3>1. API 开发和调试</h3>
            <ul>
              <li>查看 API 返回的 JSON 数据结构</li>
              <li>验证 API 响应格式是否正确</li>
              <li>对比不同 API 版本的数据差异</li>
              <li>快速定位需要的字段路径</li>
            </ul>

            <h3>2. 数据分析</h3>
            <ul>
              <li>分析日志文件中的 JSON 数据</li>
              <li>查看数据库导出的 JSON 格式</li>
              <li>对比不同时间点的数据变化</li>
            </ul>

            <h3>3. 配置文件编辑</h3>
            <ul>
              <li>编辑 package.json、tsconfig.json 等配置文件</li>
              <li>验证配置文件格式是否正确</li>
              <li>快速查找和修改配置项</li>
            </ul>

            <h3>4. 学习和教学</h3>
            <ul>
              <li>学习 JSON 数据结构</li>
              <li>理解 API 返回的数据格式</li>
              <li>演示不同的 JSON 结构</li>
            </ul>
          `
        },
        {
          title: '快捷键',
          content: `
            <table class="min-w-full">
              <thead>
                <tr>
                  <th class="text-left">快捷键</th>
                  <th class="text-left">功能</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>Ctrl/Cmd + V</code></td>
                  <td>粘贴 JSON 数据</td>
                </tr>
                <tr>
                  <td><code>Ctrl/Cmd + C</code></td>
                  <td>复制选中内容</td>
                </tr>
                <tr>
                  <td><code>Ctrl/Cmd + A</code></td>
                  <td>全选</td>
                </tr>
                <tr>
                  <td><code>Ctrl/Cmd + Z</code></td>
                  <td>撤销</td>
                </tr>
                <tr>
                  <td><code>Ctrl/Cmd + Shift + Z</code></td>
                  <td>重做</td>
                </tr>
                <tr>
                  <td><code>Ctrl/Cmd + F</code></td>
                  <td>在编辑器中查找</td>
                </tr>
              </tbody>
            </table>
          `
        }
      ]
    },
    en: {
      title: 'JSON Formatter Tool Guide',
      subtitle: 'Quickly master DevToolkit\'s JSON tool',
      sections: [
        {
          title: 'Features',
          items: [
            {
              title: 'JSON to HTML Table View',
              desc: 'Convert JSON data to HTML table format with Key/Value/Type columns, similar to Postman preview. Perfect for viewing API responses, database query results, and complex JSON structures. Supports expanding/collapsing nested objects and arrays, one-click copy paths and values. Table mode allows quick field location and significantly improves data viewing efficiency.'
            },
            {
              title: 'Tree View',
              desc: 'Traditional tree structure display, intuitively showing JSON hierarchical relationships. Quick understanding of data structure and nesting levels.'
            },
            {
              title: 'Smart Error Fixing',
              desc: 'Auto-detects common JSON format errors (trailing commas, single quotes, missing quotes, etc.), one-click fix for all issues.'
            },
            {
              title: 'Multi-Tab Comparison',
              desc: 'Support opening multiple JSON files simultaneously, easy to compare data structures from different APIs or view data changes.'
            },
            {
              title: 'Inner JSON Parsing',
              desc: 'Intelligently recognizes stringified JSON, can further parse nested JSON data.'
            },
            {
              title: 'Syntax Highlighting',
              desc: 'Code editor supports JSON syntax highlighting, different data types displayed in different colors for better readability.'
            }
          ]
        },
        {
          title: 'Tutorial',
          content: `
            <h3>1. Basic Usage</h3>
            <p><strong>Step 1: Paste or Input JSON</strong></p>
            <p>Paste your JSON data in the left editor. Supports any valid JSON format including objects, arrays, nested structures, etc.</p>

            <p><strong>Step 2: Auto Format</strong></p>
            <p>After pasting, the tool automatically detects and formats JSON. If format errors are found, error messages and fix suggestions will be displayed.</p>

            <p><strong>Step 3: Choose View Mode</strong></p>
            <ul>
              <li><strong>Table View:</strong> Click "Table" tab to view in Postman style</li>
              <li><strong>Tree View:</strong> Click "Tree" tab to view hierarchical structure</li>
              <li><strong>Code View:</strong> View formatted code directly in the editor</li>
            </ul>

            <h3>2. Table View Tips</h3>
            <p><strong>Expand/Collapse:</strong></p>
            <ul>
              <li>Click arrow icon before objects or arrays to expand/collapse</li>
              <li>Nesting levels are shown with indentation</li>
            </ul>

            <p><strong>Copy Functions:</strong></p>
            <ul>
              <li>Hover on key name, click copy icon to copy full path (like <code>user.address.city</code>)</li>
              <li>Hover on value, click copy icon to copy value content</li>
            </ul>

            <p><strong>Type Recognition:</strong></p>
            <ul>
              <li>Different types marked with different colors (string, number, boolean, object, array, null)</li>
              <li>Type column shows specific data type</li>
            </ul>

            <h3>3. Error Fixing Feature</h3>
            <p><strong>Auto Detection:</strong></p>
            <p>Tool auto-detects these common errors:</p>
            <ul>
              <li>Trailing commas (extra comma after last property)</li>
              <li>Single quotes (JSON only supports double quotes)</li>
              <li>Missing quotes on keys</li>
              <li>Comments (JSON doesn't support comments)</li>
              <li>Invalid number format</li>
            </ul>

            <p><strong>One-Click Fix:</strong></p>
            <p>When errors are detected, click "Fix" button to auto-fix all fixable errors.</p>

            <h3>4. Multi-Tab Feature</h3>
            <p><strong>New Tab:</strong></p>
            <p>Click "+" button at top to create new tab, can handle multiple JSON files simultaneously.</p>

            <p><strong>Switch Tabs:</strong></p>
            <p>Click tab name to switch to corresponding JSON data.</p>

            <p><strong>Rename Tab:</strong></p>
            <p>Double-click tab name to rename, easier to identify different JSON data.</p>

            <p><strong>Close Tab:</strong></p>
            <p>Click "×" button on tab to close unnecessary tabs.</p>

            <h3>5. Inner JSON Parsing</h3>
            <p>When a JSON value is itself a JSON string:</p>
            <ul>
              <li>In table view, the value displays as string</li>
              <li>Click "Parse Inner JSON" button</li>
              <li>Tool recognizes and parses JSON in string</li>
              <li>After parsing can continue viewing nested structure</li>
            </ul>

            <p><strong>Use Cases:</strong></p>
            <p>Some APIs return double-encoded JSON, for example:</p>
            <pre><code>{
  "data": "{\\"name\\":\\"John\\",\\"age\\":25}"
}</code></pre>
            <p>Use inner parsing to view actual data structure.</p>
          `
        },
        {
          title: 'FAQ',
          faqs: [
            {
              q: 'Is JSON data uploaded to servers?',
              a: 'No. All JSON data is processed entirely locally in your browser and never uploaded to any server. Your data is completely safe and private.'
            },
            {
              q: 'How large JSON files are supported?',
              a: 'Theoretically no size limit, but recommend not exceeding 10MB. Too large files may cause browser lag. For very large files, suggest splitting first.'
            },
            {
              q: 'How to save formatted JSON?',
              a: 'Simply copy the formatted JSON code from the editor. Or use browser "Save As" function. History is automatically saved in local browser.'
            },
            {
              q: 'Why can\'t my JSON be parsed?',
              a: 'JSON has strict format requirements. Common issues: trailing commas, single quotes, missing quotes on keys, comments, etc. Use our error fixing feature to auto-fix most problems.'
            },
            {
              q: 'What\'s the difference between table view and tree view?',
              a: 'Table view is like Postman, suitable for viewing specific key-value pairs and data types, high information density. Tree view more intuitively shows hierarchical relationships, suitable for understanding data structure. Suggestion: first view use tree view, finding specific values use table view.'
            },
            {
              q: 'How to handle JSON with Chinese characters?',
              a: 'Tool fully supports Unicode characters including Chinese, Japanese, Korean, etc. You can directly paste JSON with Chinese without any conversion.'
            },
            {
              q: 'Can I edit JSON?',
              a: 'Yes. Edit JSON directly in code editor, changes update table view and tree view in real-time. If edits cause format errors, error messages will display.'
            },
            {
              q: 'Where is history saved?',
              a: 'History is saved in browser LocalStorage, not uploaded to servers. Clearing browser data will delete history.'
            },
            {
              q: 'Is JSON5 format supported?',
              a: 'Currently not supported. Tool follows standard JSON format (RFC 7159). If your data is JSON5 format (with comments, trailing commas, etc.), please use error fixing feature to convert to standard JSON first.'
            },
            {
              q: 'Can I use on mobile?',
              a: 'Yes. Website is fully responsive, works normally on phones and tablets. Mobile layout automatically adjusts for small screens.'
            }
          ]
        },
        {
          title: 'Use Cases',
          content: `
            <h3>1. API Development and Debugging</h3>
            <ul>
              <li>View JSON data structure returned by APIs</li>
              <li>Verify API response format correctness</li>
              <li>Compare data differences between API versions</li>
              <li>Quickly locate needed field paths</li>
            </ul>

            <h3>2. Data Analysis</h3>
            <ul>
              <li>Analyze JSON data in log files</li>
              <li>View database exported JSON format</li>
              <li>Compare data changes at different times</li>
            </ul>

            <h3>3. Config File Editing</h3>
            <ul>
              <li>Edit package.json, tsconfig.json and other config files</li>
              <li>Verify config file format correctness</li>
              <li>Quickly find and modify configurations</li>
            </ul>

            <h3>4. Learning and Teaching</h3>
            <ul>
              <li>Learn JSON data structure</li>
              <li>Understand API returned data format</li>
              <li>Demonstrate different JSON structures</li>
            </ul>
          `
        },
        {
          title: 'Shortcuts',
          content: `
            <table class="min-w-full">
              <thead>
                <tr>
                  <th class="text-left">Shortcut</th>
                  <th class="text-left">Function</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>Ctrl/Cmd + V</code></td>
                  <td>Paste JSON data</td>
                </tr>
                <tr>
                  <td><code>Ctrl/Cmd + C</code></td>
                  <td>Copy selection</td>
                </tr>
                <tr>
                  <td><code>Ctrl/Cmd + A</code></td>
                  <td>Select all</td>
                </tr>
                <tr>
                  <td><code>Ctrl/Cmd + Z</code></td>
                  <td>Undo</td>
                </tr>
                <tr>
                  <td><code>Ctrl/Cmd + Shift + Z</code></td>
                  <td>Redo</td>
                </tr>
                <tr>
                  <td><code>Ctrl/Cmd + F</code></td>
                  <td>Find in editor</td>
                </tr>
              </tbody>
            </table>
          `
        }
      ]
    }
  };

  const data = content[language];

  return (
    <div className="bg-[#faf9f6] dark:bg-gray-900">
      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex gap-8">
          {/* 主内容区 */}
          <article className="flex-1 min-w-0">
            {/* 页面标题 */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-3">
                <Braces size={28} className="text-blue-600 dark:text-blue-400" />
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{data.title}</h1>
              </div>
              <p className="text-gray-600 dark:text-gray-400">{data.subtitle}</p>
            </div>

            <div className="prose prose-lg dark:prose-invert max-w-none
          prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white
          prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-200 dark:prose-h2:border-gray-700
          prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
          prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-4
          prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline
          prose-strong:text-gray-900 dark:prose-strong:text-white prose-strong:font-semibold
          prose-ul:my-4 prose-li:my-2
          prose-table:border-collapse prose-table:w-full
          prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-600 prose-th:bg-gray-100 dark:prose-th:bg-gray-800 prose-th:p-3
          prose-td:border prose-td:border-gray-300 dark:prose-td:border-gray-600 prose-td:p-3
          prose-code:text-blue-600 dark:prose-code:text-blue-400 prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
          prose-pre:bg-gray-100 dark:prose-pre:bg-gray-800 prose-pre:p-4 prose-pre:rounded-lg
        ">
          {data.sections.map((section, index) => (
            <section key={index} id={`section-${index}`}>
              <h2>
                {section.title}
              </h2>

              {section.items && (
                <div className="grid md:grid-cols-2 gap-6 not-prose">
                  {section.items.map((item, i) => (
                    <div key={i} className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                      <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{item.title}</h4>
                      <p className="text-gray-600 dark:text-gray-400">{item.desc}</p>
                    </div>
                  ))}
                </div>
              )}

              {section.content && (
                <div dangerouslySetInnerHTML={{ __html: section.content }} />
              )}

              {section.faqs && (
                <div className="space-y-6">
                  {section.faqs.map((faq, i) => (
                    <div key={i} className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-700">
                      <div className="flex items-start gap-3">
                        <HelpCircle size={20} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
                        <div>
                          <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{faq.q}</h4>
                          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{faq.a}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-12 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-8 border border-blue-200 dark:border-blue-700">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            {language === 'zh' ? '开始使用' : 'Get Started'}
          </h3>
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            {language === 'zh'
              ? '立即体验 DevToolkit 的 JSON 格式化工具，享受高效便捷的 JSON 数据处理！'
              : 'Experience DevToolkit\'s JSON formatter now for efficient and convenient JSON data processing!'}
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/json"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <Braces size={20} />
              {language === 'zh' ? '打开 JSON 工具' : 'Open JSON Tool'}
            </Link>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 font-medium transition-colors"
            >
              {language === 'zh' ? '查看更多教程' : 'More Tutorials'}
            </Link>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
          <Link
            to="/json"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 transition-colors"
          >
            <ArrowLeft size={16} />
            {language === 'zh' ? '返回 JSON 工具' : 'Back to JSON Tool'}
          </Link>
        </div>
      </article>

      {/* 右侧大纲 */}
      <aside className="hidden lg:block w-64 flex-shrink-0">
        <div className="sticky top-20">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
              </svg>
              {language === 'zh' ? '目录' : 'Contents'}
            </h2>
            <nav className="space-y-1">
              {data.sections.map((section, index) => (
                <a
                  key={index}
                  href={`#section-${index}`}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(`section-${index}`)?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className={`block px-3 py-2 text-sm rounded-lg transition-colors ${
                    activeSection === index
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium'
                      : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className={`flex-shrink-0 w-5 text-xs ${activeSection === index ? 'font-bold' : ''}`}>
                      {index + 1}.
                    </span>
                    <span className="line-clamp-2">{section.title}</span>
                  </span>
                </a>
              ))}
            </nav>
          </div>
        </div>
      </aside>
    </div>
      </div>
    </div>
  );
}

export default JsonHelp;
